import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Eye,
  PackageCheck,
  RotateCcw,
  Sparkles,
  Warehouse,
  X,
} from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  CustomDatePicker,
  FeedbackMessage,
  FilterField,
  FilterPanel,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import useReleasings from '../hooks/useReleasings';
import { formatNumericDate, formatPeso, formatShortDate } from '../utils/formatters';

const tabs = [
  { id: 'pending', label: 'Pending S.O.', icon: ClipboardList },
  { id: 'history', label: 'Release History', icon: CheckCircle2 },
  { id: 'inventory', label: 'Available Inventory', icon: Warehouse },
];
const RELEASE_HISTORY_PAGE_SIZE = 10;
const RELEASING_TABLE_PAGE_SIZE = 10;
const RELEASE_ALLOCATION_PAGE_SIZE = 5;
const RELEASE_SLOT_DRAFT_STORAGE_KEY = 'seaweeds.releaseSlotDrafts.v1';
const initialFilterValues = {
  search: '',
  pendingStatus: 'All',
  date: '',
};
const initialFiltersByTab = {
  pending: initialFilterValues,
  history: initialFilterValues,
  inventory: initialFilterValues,
};

function ReleasingPage({ navigationItems, activePath, activeSearch = '', onNavigate }) {
  const [isReleaseConfirmOpen, setIsReleaseConfirmOpen] = useState(false);
  const [isPreReleaseGuardOpen, setIsPreReleaseGuardOpen] = useState(false);
  const [releaseToReopen, setReleaseToReopen] = useState(null);
  const [hasAttemptedReleaseSave, setHasAttemptedReleaseSave] = useState(false);
  const [filterValuesByTab, setFilterValuesByTab] = useState(initialFiltersByTab);
  const [appliedFiltersByTab, setAppliedFiltersByTab] = useState(initialFiltersByTab);
  const [focusedBatchNo, setFocusedBatchNo] = useState('');
  const {
    activeTab,
    pendingSalesOrders,
    releaseHistory,
    availableInventory,
    filteredInventory,
    selectedSalesOrder,
    selectedFclSlot,
    includePreReleaseInventory,
    preReleaseCandidateCount,
    releaseDate,
    remarks,
    expensePerSack,
    allocations,
    allocationErrors,
    allocationTotals,
    allocatedKg,
    hasSelectedPreReleaseAllocations,
    allocatedOrderQuantity,
    remainingAfterAllocation,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    setActiveTab,
    setReleaseDate,
    setRemarks,
    setExpensePerSack,
    setIncludePreReleaseInventory,
    openReleaseModal,
    closeReleaseModal,
    handleAllocationChange,
    handleAutoSelectProfitableAllocations,
    handleResetAllocations,
    validateReleaseDraft,
    handleSubmitRelease,
    handleReopenRelease,
  } = useReleasings();
  const toasts = [
    loadError ? { id: 'releasing-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'releasing-submit-error', tone: 'error', message: submitError } : null,
    successMessage ? { id: 'releasing-success', tone: 'success', message: successMessage } : null,
  ].filter(Boolean);
  const filterValues = filterValuesByTab[activeTab] || initialFilterValues;
  const filteredPendingSalesOrders = useMemo(
    () => filterPendingSalesOrders(pendingSalesOrders, appliedFiltersByTab.pending || initialFilterValues),
    [appliedFiltersByTab, pendingSalesOrders]
  );
  const filteredReleaseHistory = useMemo(
    () => filterReleaseHistory(releaseHistory, appliedFiltersByTab.history || initialFilterValues),
    [appliedFiltersByTab, releaseHistory]
  );
  const filteredAvailableInventory = useMemo(
    () => filterAvailableInventory(availableInventory, appliedFiltersByTab.inventory || initialFilterValues),
    [appliedFiltersByTab, availableInventory]
  );
  const searchSuggestions = useMemo(
    () =>
      buildReleasingSearchSuggestions({
        activeTab,
        pendingSalesOrders,
        releaseHistory,
        availableInventory,
      }),
    [activeTab, availableInventory, pendingSalesOrders, releaseHistory]
  );

  useEffect(() => {
    setHasAttemptedReleaseSave(false);
  }, [selectedSalesOrder?.id, selectedFclSlot?.key]);

  useEffect(() => {
    const searchParams = new URLSearchParams(activeSearch);
    const requestedTab = searchParams.get('tab');
    const batchNo = String(searchParams.get('batch') || searchParams.get('batchNo') || '').trim();

    if (tabs.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    } else if (batchNo) {
      setActiveTab('pending');
    }

    if (!batchNo) {
      setFocusedBatchNo('');
      return;
    }

    const nextFilters = {
      ...initialFilterValues,
      search: batchNo,
    };

    setFocusedBatchNo(batchNo);
    setFilterValuesByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      pending: nextFilters,
    }));
    setAppliedFiltersByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      pending: nextFilters,
    }));
  }, [activeSearch, setActiveTab]);

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValuesByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      [activeTab]: {
        ...(currentValuesByTab[activeTab] || initialFilterValues),
        [name]: value,
      },
    }));

    if (name === 'search' || name === 'pendingStatus' || name === 'date') {
      setAppliedFiltersByTab((currentValuesByTab) => ({
        ...currentValuesByTab,
        [activeTab]: {
          ...(currentValuesByTab[activeTab] || initialFilterValues),
          [name]: value,
        },
      }));
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFiltersByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      [activeTab]: filterValuesByTab[activeTab] || initialFilterValues,
    }));
  }

  function handleFilterReset() {
    setFilterValuesByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      [activeTab]: initialFilterValues,
    }));
    setAppliedFiltersByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      [activeTab]: initialFilterValues,
    }));
  }

  function handleOpenReleaseHistory(releaseCode) {
    const nextFilters = {
      ...initialFilterValues,
      search: releaseCode,
    };

    setActiveTab('history');
    setFilterValuesByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      history: nextFilters,
    }));
    setAppliedFiltersByTab((currentValuesByTab) => ({
      ...currentValuesByTab,
      history: nextFilters,
    }));
  }

  function handleReleaseSubmitRequest(event) {
    event.preventDefault();
    setHasAttemptedReleaseSave(true);

    if (hasSelectedPreReleaseAllocations) {
      setIsPreReleaseGuardOpen(true);
      return;
    }

    if (!validateReleaseDraft()) {
      return;
    }

    setIsReleaseConfirmOpen(true);
  }

  async function handleSaveDraftRequest() {
    setHasAttemptedReleaseSave(true);

    if (!validateReleaseDraft({ saveMode: 'draft' })) {
      return;
    }

    const wasSaved = await handleSubmitRelease(
      { preventDefault: () => {} },
      { saveMode: 'draft' }
    );

    if (wasSaved) {
      setHasAttemptedReleaseSave(false);
    }
  }

  async function handleConfirmRelease() {
    const wasSaved = await handleSubmitRelease(
      { preventDefault: () => {} },
      { saveMode: 'release' }
    );

    setIsReleaseConfirmOpen(false);

    if (wasSaved) {
      return;
    }
  }

  function handleReopenReleaseRequest(salesOrder, fclRow) {
    if (!fclRow?.release?.id) {
      return;
    }

    setReleaseToReopen({
      id: fclRow.release.id,
      releaseCode: fclRow.release.releaseCode,
      batchNo: salesOrder?.soCode || '',
      fclNumber: fclRow.number,
    });
  }

  async function handleConfirmReopenRelease() {
    if (!releaseToReopen?.id) {
      return;
    }

    const wasReopened = await handleReopenRelease(releaseToReopen.id);

    if (wasReopened) {
      setReleaseToReopen(null);
    }
  }

  function handleCloseReleaseModal() {
    setIsReleaseConfirmOpen(false);
    setHasAttemptedReleaseSave(false);
    closeReleaseModal();
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      activeSearch={activeSearch}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div className="flex flex-col gap-4 rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.05)] lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="m-0 text-[1.25rem] font-semibold text-[var(--theme-text)] sm:text-[1.4rem]">
                Releasing
              </h2>
              <p className="m-0 mt-2 max-w-[760px] text-[0.95rem] leading-6 text-[var(--theme-text-muted)]">
                Release completed inventory against active Sales Orders and keep an audit trail of every allocation.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-[12px] border px-3 text-[0.84rem] font-semibold transition ${
                      isActive
                        ? 'border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] text-[var(--theme-text-strong)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.08)]'
                        : 'border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <ReleasingFilters
            activeTab={activeTab}
            values={filterValues}
            suggestions={searchSuggestions}
            onChange={handleFilterChange}
            onSubmit={handleFilterSubmit}
            onReset={handleFilterReset}
          />

          {isLoading ? (
            <FeedbackMessage tone="info" message="Loading releasing workspace..." />
          ) : activeTab === 'pending' ? (
            <PendingSalesOrdersTable
              salesOrders={filteredPendingSalesOrders}
              releaseHistory={releaseHistory}
              focusedBatchNo={focusedBatchNo}
              onRelease={openReleaseModal}
              onReopenRelease={handleReopenReleaseRequest}
              isReopeningRelease={isSaving}
              onOpenReleaseHistory={handleOpenReleaseHistory}
            />
          ) : activeTab === 'history' ? (
            <ReleaseHistoryTable releases={filteredReleaseHistory} />
          ) : (
            <AvailableInventoryTable inventory={filteredAvailableInventory} />
          )}
        </div>
      </section>

      {selectedSalesOrder ? (
        <ReleaseInventoryModal
          salesOrder={selectedSalesOrder}
          fclSlot={selectedFclSlot}
          inventory={filteredInventory}
          releaseDate={releaseDate}
          remarks={remarks}
          expensePerSack={expensePerSack}
          includePreReleaseInventory={includePreReleaseInventory}
          preReleaseCandidateCount={preReleaseCandidateCount}
          allocations={allocations}
          allocationErrors={allocationErrors}
          allocationTotals={allocationTotals}
          hasAttemptedReleaseSave={hasAttemptedReleaseSave}
          isSaving={isSaving}
          onDateChange={setReleaseDate}
          onRemarksChange={setRemarks}
          onExpensePerSackChange={setExpensePerSack}
          onIncludePreReleaseInventoryChange={setIncludePreReleaseInventory}
          onAllocationChange={handleAllocationChange}
          onAutoSelectAllocations={handleAutoSelectProfitableAllocations}
          onResetAllocations={handleResetAllocations}
          onSaveDraft={handleSaveDraftRequest}
          onClose={handleCloseReleaseModal}
          onSubmit={handleReleaseSubmitRequest}
        />
      ) : null}
      <SaveConfirmationModal
        isOpen={isReleaseConfirmOpen}
        isConfirming={isSaving}
        title="Confirm release"
        message={`Release ${formatSalesOrderQuantity(allocatedOrderQuantity)} (${formatKg(allocatedKg)}) against batch ${selectedSalesOrder?.soCode || ''}? Remaining after release will be ${formatSalesOrderQuantity(remainingAfterAllocation)}.`}
        confirmLabel="Confirm Release"
        confirmingLabel="Releasing..."
        cancelLabel="Review"
        onCancel={() => setIsReleaseConfirmOpen(false)}
        onConfirm={handleConfirmRelease}
      />
      <SaveConfirmationModal
        isOpen={Boolean(releaseToReopen)}
        isConfirming={isSaving}
        title="Reopen released FCL?"
        message={`Reopen ${releaseToReopen?.releaseCode || 'this release'} from batch ${releaseToReopen?.batchNo || '-'}${releaseToReopen?.fclNumber ? ` FCL ${releaseToReopen.fclNumber}` : ''} as a draft? You can review and release it again after reopening.`}
        confirmLabel="Reopen"
        confirmingLabel="Reopening..."
        cancelLabel="Cancel"
        onCancel={() => setReleaseToReopen(null)}
        onConfirm={handleConfirmReopenRelease}
      />
      <SaveConfirmationModal
        isOpen={isPreReleaseGuardOpen}
        isConfirming={false}
        title="Pre-release P.O. selected"
        message="Save Release is not available while pre-release P.O.s are selected. Save this as a draft first, then process the release after the selected P.O.s are completed in Supplier Balancing."
        confirmLabel="Review Selection"
        cancelLabel="Close"
        onCancel={() => setIsPreReleaseGuardOpen(false)}
        onConfirm={() => setIsPreReleaseGuardOpen(false)}
      />
    </AppShell>
  );
}

function PendingSalesOrdersTable({
  salesOrders,
  releaseHistory,
  focusedBatchNo = '',
  onRelease,
  onReopenRelease,
  isReopeningRelease = false,
  onOpenReleaseHistory,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSalesOrderIds, setExpandedSalesOrderIds] = useState(() => new Set());
  const [slotDrafts, setSlotDrafts] = useState(loadReleaseSlotDrafts);
  const totalItems = salesOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / RELEASING_TABLE_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * RELEASING_TABLE_PAGE_SIZE;
  const endIndex = Math.min(startIndex + RELEASING_TABLE_PAGE_SIZE, totalItems);
  const paginatedSalesOrders = salesOrders.slice(startIndex, endIndex);

  useEffect(() => {
    setExpandedSalesOrderIds((currentIds) => {
      const visibleOrderIds = new Set(salesOrders.map((salesOrder) => salesOrder.id));
      const nextIds = new Set(
        Array.from(currentIds).filter((salesOrderId) => visibleOrderIds.has(salesOrderId))
      );

      return nextIds.size === currentIds.size ? currentIds : nextIds;
    });
  }, [salesOrders]);

  useEffect(() => {
    const normalizedFocusedBatchNo = normalizeSearchValue(focusedBatchNo);

    if (!normalizedFocusedBatchNo) {
      return;
    }

    const matchingSalesOrderIndex = salesOrders.findIndex(
      (salesOrder) => normalizeSearchValue(getSalesOrderBatchNo(salesOrder)) === normalizedFocusedBatchNo
    );

    if (matchingSalesOrderIndex < 0) {
      return;
    }

    const matchingSalesOrder = salesOrders[matchingSalesOrderIndex];
    const nextPage = Math.floor(matchingSalesOrderIndex / RELEASING_TABLE_PAGE_SIZE) + 1;

    setCurrentPage(nextPage);
    setExpandedSalesOrderIds((currentIds) => {
      if (currentIds.has(matchingSalesOrder.id)) {
        return currentIds;
      }

      return new Set([...currentIds, matchingSalesOrder.id]);
    });
  }, [focusedBatchNo, salesOrders]);

  function handleToggleExpand(salesOrderId) {
    setExpandedSalesOrderIds((currentIds) => {
      const nextIds = new Set(currentIds);

      if (nextIds.has(salesOrderId)) {
        nextIds.delete(salesOrderId);
      } else {
        nextIds.add(salesOrderId);
      }

      return nextIds;
    });
  }

  function handleSlotDraftChange(salesOrder, fclRow, fieldName, value) {
    const draftKey = getReleaseSlotDraftKey(salesOrder, fclRow);
    const normalizedValue = normalizeReleaseSlotDraftInput(value);

    setSlotDrafts((currentDrafts) => {
      const nextDrafts = {
        ...currentDrafts,
        [draftKey]: {
          ...(currentDrafts[draftKey] || {}),
          [fieldName]: normalizedValue,
        },
      };

      saveReleaseSlotDrafts(nextDrafts);
      return nextDrafts;
    });
  }

  if (!salesOrders.length) {
    return (
      <EmptyPanel
        title="No pending Sales Orders"
        message="Active orders with remaining quantity will appear here once inventory is ready to release."
      />
    );
  }

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 lg:hidden">
        {paginatedSalesOrders.map((salesOrder) => {
          const isExpanded = expandedSalesOrderIds.has(salesOrder.id);
          const fclRows = buildSalesOrderFclRows(salesOrder, releaseHistory);
          const pendingFclCount = fclRows.filter((row) => row.status !== 'Released').length;

          return (
            <article
              key={salesOrder.id}
              className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)]"
            >
              <div className="grid gap-3 p-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[1rem] font-semibold text-[var(--theme-text)]">
                      {salesOrder.soCode}
                    </div>
                    <div className="mt-0.5 truncate text-[0.84rem] text-[var(--theme-text-muted)]">
                      {salesOrder.customerName || '-'} / {salesOrder.branch || '-'}
                    </div>
                  </div>
                  <StatusPill label={salesOrder.status} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <MobileBatchMetric label="Requested" value={formatSalesOrderQuantity(salesOrder.requestedQuantity)} />
                  <MobileBatchMetric label="Released" value={formatSalesOrderQuantity(salesOrder.releasedQuantity)} />
                  <MobileBatchMetric label="KG Released" value={formatKg(salesOrder.releasedKg ?? salesOrder.releasedQuantity)} />
                  <MobileBatchMetric
                    label="Remaining"
                    value={<RemainingBadge value={formatSalesOrderQuantity(salesOrder.remainingQuantity)} />}
                  />
                  <MobileBatchMetric label="S.O. Date" value={formatNumericDate(salesOrder.soDate)} />
                  <MobileBatchMetric label="FCL Slots" value={`${fclRows.length} FCL`} />
                </div>
                <button
                  type="button"
                  className={`inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-[12px] border px-3 text-[0.86rem] font-semibold transition ${
                    isExpanded
                      ? 'border-[var(--theme-accent)] bg-[var(--theme-accent)] text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.14)]'
                      : 'border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]'
                  }`}
                  onClick={() => handleToggleExpand(salesOrder.id)}
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} batch ${salesOrder.soCode} FCL rows`}
                >
                  {isExpanded ? (
                    <ChevronDown size={17} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={17} aria-hidden="true" />
                  )}
                  <span>{isExpanded ? 'Collapse FCL Slots' : 'View FCL Slots'}</span>
                </button>
              </div>
              {isExpanded ? (
                <div className="border-t border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-3">
                  <FclRowsPanel
                    salesOrder={salesOrder}
                    fclRows={fclRows}
                    pendingFclCount={pendingFclCount}
                    slotDrafts={slotDrafts}
                    onSlotDraftChange={handleSlotDraftChange}
                    onRelease={onRelease}
                    onReopenRelease={onReopenRelease}
                    isReopeningRelease={isReopeningRelease}
                    onOpenReleaseHistory={onOpenReleaseHistory}
                  />
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)] lg:block">
        <div className="overflow-x-auto">
          <table className="min-w-[1020px] w-full border-collapse text-left text-[0.9rem]">
            <thead className="bg-[var(--theme-accent-panel)] text-[0.75rem] uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
              <tr>
                <th className="px-4 py-3">Batch No.</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Released</th>
                <th className="px-4 py-3">KG Released</th>
                <th className="px-4 py-3">Remaining</th>
                <th className="px-4 py-3">S.O. Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-border-soft)]">
              {paginatedSalesOrders.map((salesOrder) => {
                const isExpanded = expandedSalesOrderIds.has(salesOrder.id);
                const fclRows = buildSalesOrderFclRows(salesOrder, releaseHistory);
                const pendingFclCount = fclRows.filter((row) => row.status !== 'Released').length;

                return (
                  <Fragment key={salesOrder.id}>
                    <tr key={salesOrder.id} className="align-middle">
                      <td className="px-4 py-3 font-semibold text-[var(--theme-text)]">{salesOrder.soCode}</td>
                      <td className="px-4 py-3 text-[var(--theme-text-strong)]">{salesOrder.customerName || '-'}</td>
                      <td className="px-4 py-3 text-[var(--theme-text-subtle)]">{salesOrder.branch || '-'}</td>
                      <td className="px-4 py-3">
                        {formatSalesOrderQuantity(salesOrder.requestedQuantity)}
                      </td>
                      <td className="px-4 py-3">
                        {formatSalesOrderQuantity(salesOrder.releasedQuantity)}
                      </td>
                      <td className="px-4 py-3">
                        {formatKg(salesOrder.releasedKg ?? salesOrder.releasedQuantity)}
                      </td>
                      <td className="px-4 py-3">
                        <RemainingBadge value={formatSalesOrderQuantity(salesOrder.remainingQuantity)} />
                      </td>
                      <td className="px-4 py-3">{formatNumericDate(salesOrder.soDate)}</td>
                      <td className="px-4 py-3"><StatusPill label={salesOrder.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className={`inline-flex min-h-[38px] items-center justify-center gap-2 rounded-[12px] border px-3 text-[0.84rem] font-semibold transition ${
                            isExpanded
                              ? 'border-[var(--theme-accent)] bg-[var(--theme-accent)] text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.14)]'
                              : 'border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]'
                          }`}
                          onClick={() => handleToggleExpand(salesOrder.id)}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} batch ${salesOrder.soCode} FCL rows`}
                        >
                          {isExpanded ? (
                            <ChevronDown size={17} aria-hidden="true" />
                          ) : (
                            <ChevronRight size={17} aria-hidden="true" />
                          )}
                          <span>{isExpanded ? 'Collapse' : `${fclRows.length} FCL`}</span>
                        </button>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr key={`${salesOrder.id}-fcl-rows`} className="bg-[var(--theme-surface-soft)]">
                        <td colSpan={10} className="px-2 py-3 sm:px-4">
                          <FclRowsPanel
                            salesOrder={salesOrder}
                            fclRows={fclRows}
                            pendingFclCount={pendingFclCount}
                            slotDrafts={slotDrafts}
                            onSlotDraftChange={handleSlotDraftChange}
                            onRelease={onRelease}
                            onReopenRelease={onReopenRelease}
                            isReopeningRelease={isReopeningRelease}
                            onOpenReleaseHistory={onOpenReleaseHistory}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ReleasingPagination
        currentPage={normalizedPage}
        endItem={endIndex}
        onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
        onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
        startItem={startIndex + 1}
        totalItems={totalItems}
        totalPages={totalPages}
      />
    </div>
  );
}

function MobileBatchMetric({ label, value }) {
  return (
    <div className="min-w-0 rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-3 py-2">
      <div className="truncate text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
        {label}
      </div>
      <div className="mt-1 min-w-0 text-[0.86rem] font-semibold text-[var(--theme-text)]">
        {value}
      </div>
    </div>
  );
}

function RemainingBadge({ value }) {
  return (
    <span className="inline-flex min-h-[30px] w-fit max-w-full items-center rounded-full border border-[#ead7a8] bg-[#fff8e6] px-3 text-[0.82rem] font-bold text-[#8a641f] shadow-[0_6px_14px_rgba(138,100,31,0.08)]">
      <span className="truncate">{value}</span>
    </span>
  );
}

function FclRowsPanel({
  salesOrder,
  fclRows,
  pendingFclCount,
  slotDrafts,
  onSlotDraftChange,
  onRelease,
  onReopenRelease,
  isReopeningRelease = false,
  onOpenReleaseHistory,
}) {
  return (
    <div className="max-w-full rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-3 shadow-[inset_4px_0_0_var(--theme-accent)]">
      <div className="mb-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="truncate text-[0.9rem] font-semibold text-[var(--theme-text)]">
            Batch {salesOrder.soCode} FCL release slots
          </div>
          <div className="text-[0.8rem] text-[var(--theme-text-muted)]">
            {pendingFclCount} pending of {fclRows.length} FCL
          </div>
        </div>
        <span className="inline-flex w-fit max-w-full rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 py-1 text-[0.75rem] font-semibold text-[var(--theme-accent-strong)]">
          Child rows belong to this batch.
        </span>
      </div>
      <div className="grid min-w-0 gap-2">
        {fclRows.map((fclRow) => {
          const draftKey = getReleaseSlotDraftKey(salesOrder, fclRow);
          const slotDraft = slotDrafts[draftKey] || {};
          const isSavedDraft = fclRow.status === 'Saved as Draft';
          const releaseSlotDraft = fclRow.release
            ? {
                sacks: fclRow.release.slotSacks,
                grossKg: fclRow.release.slotGrossKg,
              }
            : {};
          const effectiveSlotDraft =
            fclRow.release && !isSavedDraft
              ? releaseSlotDraft
              : {
                  sacks: Object.prototype.hasOwnProperty.call(slotDraft, 'sacks')
                    ? slotDraft.sacks
                    : releaseSlotDraft.sacks,
                  grossKg: Object.prototype.hasOwnProperty.call(slotDraft, 'grossKg')
                    ? slotDraft.grossKg
                    : releaseSlotDraft.grossKg,
                };
          const hasValidSlotDraft = isReleaseSlotDraftValid(effectiveSlotDraft);

          return (
            <div
              key={fclRow.key}
              className="grid min-w-0 gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-3 lg:grid-cols-[90px_minmax(170px,1fr)_240px_190px_150px_auto] lg:items-center"
            >
              <div className="font-semibold text-[var(--theme-text)]">
                FCL {fclRow.number}
              </div>
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-[0.88rem] text-[var(--theme-text-strong)]">
                  <span className="inline-flex min-h-[26px] items-center rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-2.5 text-[0.75rem] font-semibold text-[var(--theme-accent-strong)]">
                    {fclRow.slotCode}
                  </span>
                  {fclRow.release ? (
                    <span className="min-w-0">
                      {isSavedDraft ? 'Reopened as' : 'Released as'}{' '}
                      {isSavedDraft ? (
                        <span className="font-semibold text-[var(--theme-accent-strong)]">
                          {fclRow.release.releaseCode}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="font-semibold text-[var(--theme-accent-strong)] underline-offset-4 transition hover:text-[var(--theme-accent)] hover:underline"
                          onClick={() => onOpenReleaseHistory?.(fclRow.release.releaseCode)}
                        >
                          {fclRow.release.releaseCode}
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="min-w-0 truncate">Pending batch assignment</span>
                  )}
                </div>
                <div className="text-[0.78rem] text-[var(--theme-text-muted)]">
                  {fclRow.release
                    ? `${isSavedDraft ? 'Reopened from' : 'Released on'} ${formatShortDate(fclRow.release.releaseDate)}`
                    : 'Release this FCL from available inventory'}
                </div>
              </div>
              <ReleaseSlotDraftInputs
                disabled={fclRow.status === 'Released'}
                grossKg={effectiveSlotDraft.grossKg}
                sacks={effectiveSlotDraft.sacks}
                onGrossKgChange={(value) =>
                  onSlotDraftChange(salesOrder, fclRow, 'grossKg', value)
                }
                onSacksChange={(value) =>
                  onSlotDraftChange(salesOrder, fclRow, 'sacks', value)
                }
              />
              <div className="grid min-w-0 gap-1 self-center text-[0.78rem] lg:place-self-center lg:justify-self-start">
                <ReleaseSlotMetric
                  sacks={formatSacksQuantity(fclRow.release?.releasedSacks || 0)}
                  netKg={formatKg(fclRow.release?.releasedKg || 0)}
                />
              </div>
              <div>
                <StatusPill label={fclRow.status} />
              </div>
              <div className="flex min-w-0 justify-start lg:justify-end">
                {fclRow.status === 'Released' ? (
                  <button
                    type="button"
                    className="inline-flex min-h-[36px] w-full items-center justify-center gap-2 rounded-[12px] border border-[var(--theme-border)] bg-white px-3 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
                    disabled={isReopeningRelease}
                    onClick={() => onReopenRelease?.(salesOrder, fclRow)}
                    title="Reopen this released FCL as a draft"
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    {isReopeningRelease ? 'Reopening...' : 'Reopen'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="inline-flex min-h-[36px] w-full items-center justify-center gap-2 rounded-[12px] bg-[var(--theme-accent)] px-3 text-[0.82rem] font-semibold text-white shadow-[0_8px_16px_rgba(var(--theme-shadow-rgb),0.12)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:opacity-60 disabled:shadow-none lg:w-auto"
                    disabled={!hasValidSlotDraft}
                    onClick={() => onRelease(salesOrder, { ...fclRow, draft: effectiveSlotDraft })}
                    title={
                      hasValidSlotDraft
                        ? undefined
                        : 'Enter valid sacks and gross KG before releasing this FCL.'
                    }
                  >
                    <PackageCheck size={15} aria-hidden="true" />
                    Release FCL
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReleaseSlotMetric({ sacks, netKg }) {
  return (
    <div className="inline-flex min-h-[34px] w-fit max-w-full items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-2.5 text-[0.76rem]">
      <span className="shrink-0 font-semibold uppercase tracking-[0.04em] text-[var(--theme-text-subtle)]">
        Rel.
      </span>
      <span className="min-w-0 max-w-[70px] truncate font-semibold text-[var(--theme-text)]">
        {sacks}
      </span>
      <span className="text-[var(--theme-text-muted)]">/</span>
      <span className="min-w-0 max-w-[86px] truncate font-bold text-[var(--theme-accent-strong)]">
        {netKg}
      </span>
    </div>
  );
}

function buildSalesOrderFclRows(salesOrder, releaseHistory) {
  const requestedFclCount = Math.max(0, Math.ceil(Number(salesOrder.requestedQuantity || 0)));
  const matchingReleases = releaseHistory
    .filter((release) => Number(release.salesOrderId) === Number(salesOrder.id))
    .sort((firstRelease, secondRelease) => {
      const firstDate = new Date(firstRelease.releaseDate || 0).getTime();
      const secondDate = new Date(secondRelease.releaseDate || 0).getTime();

      return firstDate - secondDate || Number(firstRelease.id || 0) - Number(secondRelease.id || 0);
  });
  const releasesBySlotCode = buildReleaseSlotMap(matchingReleases);
  const unassignedReleases = matchingReleases.filter((release) => !String(release.slotCode || '').trim());

  return Array.from({ length: requestedFclCount }, (_, index) => {
    const slotCode = formatFclSlotCode(salesOrder, index);
    const release = releasesBySlotCode.get(slotCode) || unassignedReleases.shift() || null;
    const isDraft = String(release?.status || '') === 'Pre-Released';
    const isReleased = Boolean(release) && !isDraft;

    return {
      key: `${salesOrder.id}-fcl-${index + 1}`,
      number: index + 1,
      slotCode,
      release,
      status: isDraft ? 'Saved as Draft' : isReleased ? 'Released' : 'Pending Release',
    };
  });
}

function buildReleaseSlotMap(releases) {
  return releases.reduce((releaseMap, release) => {
    const slotCode = String(release.slotCode || '').trim();

    if (!slotCode) {
      return releaseMap;
    }

    const existingRelease = releaseMap.get(slotCode);
    const isDraft = String(release.status || '') === 'Pre-Released';
    const existingIsDraft = String(existingRelease?.status || '') === 'Pre-Released';

    if (!existingRelease || (existingIsDraft && !isDraft)) {
      releaseMap.set(slotCode, release);
    }

    return releaseMap;
  }, new Map());
}

function formatFclSlotCode(salesOrder, index) {
  const prefix = String(salesOrder.fclSlotPrefix || '').trim() || getFclSlotPrefix(salesOrder.branch);
  const startSequence = Number(salesOrder.fclSlotStartSequence || 1);
  const sequence = startSequence + index;

  return `${prefix}-${String(sequence).padStart(3, '0')}`;
}

function getFclSlotPrefix(branch) {
  const match = String(branch || '').trim().match(/[A-Za-z]/);

  return match ? match[0].toUpperCase() : 'B';
}

function getReleaseSlotDraftKey(salesOrder, fclRow) {
  return `${salesOrder.id || salesOrder.soCode || 'sales-order'}:${fclRow.slotCode || fclRow.number}`;
}

function loadReleaseSlotDrafts() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const parsedDrafts = JSON.parse(
      window.localStorage.getItem(RELEASE_SLOT_DRAFT_STORAGE_KEY) || '{}'
    );

    return parsedDrafts && typeof parsedDrafts === 'object' ? parsedDrafts : {};
  } catch (_error) {
    return {};
  }
}

function saveReleaseSlotDrafts(drafts) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(RELEASE_SLOT_DRAFT_STORAGE_KEY, JSON.stringify(drafts));
}

function normalizeReleaseSlotDraftInput(value) {
  const normalizedValue = String(value || '').replace(/,/g, '').trim();

  if (!normalizedValue) {
    return '';
  }

  const [integerPart, ...decimalParts] = normalizedValue.replace(/[^\d.]/g, '').split('.');
  const decimalPart = decimalParts.join('');

  return decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
}

function formatReleaseSlotDraftNumber(value) {
  const normalizedValue = normalizeReleaseSlotDraftInput(value);

  if (!normalizedValue) {
    return '';
  }

  const [integerPart, decimalPart] = normalizedValue.split('.');
  const formattedInteger = integerPart
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(integerPart))
    : '';

  return typeof decimalPart === 'string'
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
}

function isReleaseSlotDraftValid(draft = {}) {
  return (
    parseReleaseSlotDraftNumber(draft.sacks) > 0 &&
    parseReleaseSlotDraftNumber(draft.grossKg) > 0
  );
}

function parseReleaseSlotDraftNumber(value) {
  const normalizedValue = normalizeReleaseSlotDraftInput(value);

  if (!normalizedValue || normalizedValue === '.') {
    return 0;
  }

  const numericValue = Number(normalizedValue);

  return Number.isFinite(numericValue) ? numericValue : 0;
}

function ReleasingFilters({ activeTab, values, suggestions, onChange, onSubmit, onReset }) {
  const isPendingTab = activeTab === 'pending';
  const dateLabel =
    activeTab === 'history'
      ? 'Release Date'
      : activeTab === 'inventory'
        ? 'Received Date'
        : 'Delivery Date';
  const searchPlaceholder =
    activeTab === 'history'
      ? 'Search release no., batch no., customer, source P.O.'
      : activeTab === 'inventory'
        ? 'Search P.O., supplier, warehouse'
        : 'Search batch no., customer, branch, status';

  return (
    <FilterPanel
      onSubmit={onSubmit}
      onReset={onReset}
      showApply={false}
      layoutClassName="grid gap-2.5 md:grid-cols-[minmax(260px,520px)_minmax(0,1fr)_190px_auto]"
    >
      <FilterField
        label="Search"
        name="search"
        type="search"
        value={values.search}
        onChange={onChange}
        placeholder={searchPlaceholder}
        suggestions={suggestions}
      />
      <span className="hidden md:block" aria-hidden="true" />
      {isPendingTab ? (
        <FilterField
          label="Status"
          name="pendingStatus"
          value={values.pendingStatus}
          onChange={onChange}
          options={[
            { value: 'All', label: 'All Statuses' },
            { value: 'Pending Release', label: 'Pending Release' },
            { value: 'Partially Released', label: 'Partially Released' },
          ]}
        />
      ) : (
        <FilterField
          label={dateLabel}
          name="date"
          type="date"
          value={values.date}
          onChange={onChange}
        />
      )}
    </FilterPanel>
  );
}

function ReleaseHistoryTable({ releases }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const totalItems = releases.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / RELEASE_HISTORY_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * RELEASE_HISTORY_PAGE_SIZE;
  const endIndex = Math.min(startIndex + RELEASE_HISTORY_PAGE_SIZE, totalItems);
  const paginatedReleases = releases.slice(startIndex, endIndex);

  if (!releases.length) {
    return (
      <EmptyPanel
        title="No releases yet"
        message="Saved release records will appear here with their source P.O. and released KG."
      />
    );
  }

  return (
    <div className="grid gap-3">
      <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)]">
        <div className="overflow-x-auto">
          <table className="min-w-[1060px] w-full border-collapse text-left text-[0.9rem]">
            <thead className="bg-[var(--theme-accent-panel)] text-[0.75rem] uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
              <tr>
                <th className="px-4 py-3">Release No.</th>
                <th className="px-4 py-3">Batch No.</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Released KG</th>
                <th className="px-4 py-3">Release Date</th>
                <th className="px-4 py-3">Prepared By</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-border-soft)]">
              {paginatedReleases.map((release) => (
                <tr key={release.id}>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 font-semibold text-[var(--theme-accent-strong)] underline-offset-4 transition hover:text-[var(--theme-accent)] hover:underline"
                      onClick={() => setSelectedRelease(release)}
                      title="View release details"
                    >
                      <span>{release.releaseCode}</span>
                      <Eye size={13} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </td>
                  <td className="px-4 py-3">{release.soCode || '-'}</td>
                  <td className="px-4 py-3">{release.customerName || '-'}</td>
                  <td className="px-4 py-3">{release.supplierNames || '-'}</td>
                  <td className="px-4 py-3 font-semibold text-[var(--theme-accent-strong)]">{formatKg(release.releasedKg)}</td>
                  <td className="px-4 py-3">{formatShortDate(release.releaseDate)}</td>
                  <td className="px-4 py-3">{release.preparedBy || '-'}</td>
                  <td className="px-4 py-3"><StatusPill label={release.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ReleasingPagination
        currentPage={normalizedPage}
        endItem={endIndex}
        onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
        onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
        startItem={startIndex + 1}
        totalItems={totalItems}
        totalPages={totalPages}
      />
      {selectedRelease ? (
        <ReleaseHistoryDetailModal
          release={selectedRelease}
          onClose={() => setSelectedRelease(null)}
        />
      ) : null}
    </div>
  );
}

function ReleaseHistoryDetailModal({ release, onClose }) {
  const items = Array.isArray(release.items) ? release.items : [];
  const expensePerSack = Number(release.expensePerSack || 0);
  const totalFclExpense = Number(release.releasedSacks || 0) * expensePerSack;

  return (
    <ModalBackdrop>
      <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-[980px] flex-col overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_24px_70px_rgba(64,52,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--theme-border-soft)] px-5 py-4">
          <div className="min-w-0">
            <h2 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
              {release.releaseCode}
            </h2>
            <div className="mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
              {release.soCode || '-'} · {release.customerName || '-'} · {formatShortDate(release.releaseDate)}
            </div>
          </div>
          <button
            type="button"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--theme-border)] text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            onClick={onClose}
            aria-label="Close release details"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile label="Slot Code" value={release.slotCode || '-'} />
            <SummaryTile label="Released Sacks" value={formatSacksQuantity(release.releasedSacks || 0)} />
            <SummaryTile label="Released KG" value={formatKg(release.releasedKg || 0)} strong />
            <SummaryTile label="Status" value={release.status || '-'} />
          </div>

          <div className="mt-4 overflow-hidden rounded-[14px] border border-[var(--theme-border-soft)]">
            <div className="bg-[var(--theme-accent-panel)] px-4 py-3 text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
              Released P.O.s
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full border-collapse text-left text-[0.84rem]">
                <thead className="bg-[var(--theme-surface-soft)] text-[0.68rem] uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
                  <tr>
                    <th className="px-4 py-2.5">P.O.</th>
                    <th className="px-4 py-2.5">Sacks Mark</th>
                    <th className="px-4 py-2.5">Warehouse</th>
                    <th className="px-4 py-2.5">Sacks</th>
                    <th className="px-4 py-2.5">Net KG</th>
                    <th className="px-4 py-2.5">Unit Cost</th>
                    <th className="px-4 py-2.5">FCL Expense</th>
                    <th className="px-4 py-2.5">Total Capital</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-border-soft)]">
                  {items.length ? (
                    items.map((item) => {
                      const totalCapital = Number(item.subtotal || 0) + totalFclExpense;

                      return (
                        <tr key={item.id || item.sourceKey}>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[var(--theme-text)]">{item.poCode || '-'}</div>
                            <div className="text-[0.72rem] text-[var(--theme-text-muted)]">
                              {getReleaseItemStatusLabel(item)}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-text)]">
                            {getInventorySacksMark(item)}
                          </td>
                          <td className="px-4 py-3">{item.warehouseLocation || '-'}</td>
                          <td className="px-4 py-3 font-semibold">{formatSacksQuantity(item.releaseQuantity || 0)}</td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-accent-strong)]">{formatKg(item.quantityKg || 0)}</td>
                          <td className="px-4 py-3">{formatPesoWithDecimals(item.unitCost || 0)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex min-h-[46px] flex-col justify-center rounded-[12px] border border-[#d6e3df] bg-[#f5fbf9] px-3 py-1 text-[#496862] shadow-[0_6px_14px_rgba(73,104,98,0.08)]">
                              <span className="text-[0.78rem] font-bold leading-tight">
                                {formatPeso(expensePerSack)} / sack
                              </span>
                              <span className="text-[0.7rem] font-semibold leading-tight">
                                {formatPeso(totalFclExpense)} Tot. Exp.
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-text)]">
                            {formatPeso(totalCapital)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-7 text-center text-[var(--theme-text-muted)]" colSpan={8}>
                        No P.O. items recorded for this release.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {release.remarks ? (
            <div className="mt-4 rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-4 py-3 text-[0.86rem] text-[var(--theme-text)]">
              <div className="mb-1 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
                Remarks
              </div>
              {release.remarks}
            </div>
          ) : null}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function ReleasingPagination({
  currentPage,
  endItem,
  onNext,
  onPrevious,
  startItem,
  totalItems,
  totalPages,
}) {
  return (
    <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
      <PaginationFooter
        currentPage={currentPage}
        endItem={endItem}
        isNextDisabled={currentPage >= totalPages}
        isPreviousDisabled={currentPage <= 1}
        onNext={onNext}
        onPrevious={onPrevious}
        startItem={startItem}
        totalItems={totalItems}
        totalPages={totalPages}
      />
    </div>
  );
}

function AvailableInventoryTable({ inventory }) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalItems = inventory.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / RELEASING_TABLE_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * RELEASING_TABLE_PAGE_SIZE;
  const endIndex = Math.min(startIndex + RELEASING_TABLE_PAGE_SIZE, totalItems);
  const paginatedInventory = inventory.slice(startIndex, endIndex);

  if (!inventory.length) {
    return (
      <EmptyPanel
        title="No completed inventory available"
        message="Completed receiving items with available KG will appear here oldest first."
      />
    );
  }

  return (
    <div className="grid gap-3">
      <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)]">
        <div className="overflow-x-auto">
          <table className="min-w-[1080px] w-full border-collapse text-left text-[0.9rem]">
            <thead className="bg-[var(--theme-accent-panel)] text-[0.75rem] uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
              <tr>
                <th className="px-4 py-3">P.O. / Supplier</th>
                <th className="px-4 py-3">Warehouse</th>
                <th className="px-4 py-3">Sacks / Quantity</th>
                <th className="px-4 py-3">KG Net</th>
                <th className="px-4 py-3">Released</th>
                <th className="px-4 py-3">Available</th>
                <th className="px-4 py-3">Unit Cost</th>
                <th className="px-4 py-3">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-border-soft)]">
              {paginatedInventory.map((item) => {
                const availableSacks = getAvailableSacks(item);
                const releasedSacks = getReleasedSacks(item);

                return (
                  <tr key={item.sourceKey || item.id}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--theme-text)]">{item.poCode}</div>
                      <div className="text-[0.8rem] text-[var(--theme-text-muted)]">{item.supplierName || '-'}</div>
                    </td>
                    <td className="px-4 py-3">{item.warehouseLocation || '-'}</td>
                    <td className="px-4 py-3">{formatInventoryQuantity(item)}</td>
                    <td className="px-4 py-3">{formatKg(item.originalKg)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex min-h-[42px] flex-col justify-center rounded-[12px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-1 text-[#8a641f] shadow-[0_6px_14px_rgba(138,100,31,0.08)]">
                        <span className="text-[0.8rem] font-bold leading-tight">{formatSacksQuantity(releasedSacks)}</span>
                        <span className="text-[0.72rem] font-semibold leading-tight">{formatKg(item.releasedKg)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex min-h-[42px] flex-col justify-center rounded-[12px] border border-[#b9d9b6] bg-[#eef8ec] px-3 py-1 text-[#3f733f] shadow-[0_6px_14px_rgba(63,115,63,0.08)]">
                        <span className="text-[0.8rem] font-bold leading-tight">{formatSacksQuantity(availableSacks)}</span>
                        <span className="text-[0.72rem] font-semibold leading-tight">{formatKg(item.availableKg)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatPesoWithDecimals(item.unitCost)}</td>
                    <td className="px-4 py-3">{formatShortDate(item.receivedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ReleasingPagination
        currentPage={normalizedPage}
        endItem={endIndex}
        onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
        onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
        startItem={startIndex + 1}
        totalItems={totalItems}
        totalPages={totalPages}
      />
    </div>
  );
}

function ReleaseInventoryModal({
  salesOrder,
  fclSlot,
  inventory,
  releaseDate,
  remarks,
  expensePerSack,
  includePreReleaseInventory,
  preReleaseCandidateCount,
  allocations,
  allocationErrors,
  allocationTotals,
  hasAttemptedReleaseSave,
  isSaving,
  onDateChange,
  onRemarksChange,
  onExpensePerSackChange,
  onIncludePreReleaseInventoryChange,
  onAllocationChange,
  onAutoSelectAllocations,
  onResetAllocations,
  onSaveDraft,
  onClose,
  onSubmit,
}) {
  const releasedSacks = Number(allocationTotals.orderQuantity || 0);
  const totalNetKilo = Number(allocationTotals.kg || 0);
  const totalCapital = Number(allocationTotals.capital || 0);
  const totalExpense = releasedSacks * Number(expensePerSack || 0);
  const fclCapital = totalCapital + totalExpense;
  const slotDraftSacks = parseReleaseSlotDraftNumber(fclSlot?.draft?.sacks);
  const slotDraftGrossKg = parseReleaseSlotDraftNumber(fclSlot?.draft?.grossKg);
  const slotDraftNetKg =
    slotDraftSacks > 0 && slotDraftGrossKg > 0
      ? Math.max(0, slotDraftGrossKg - slotDraftSacks / 2)
      : 0;

  return (
    <ModalBackdrop>
      <form
        className="flex max-h-[calc(100vh-3rem)] w-full max-w-[1160px] flex-col overflow-hidden rounded-[20px] border border-[var(--theme-border-soft)] bg-white shadow-[0_24px_70px_rgba(64,52,42,0.24)]"
        onSubmit={onSubmit}
      >
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-[var(--theme-border-soft)] px-5 py-4">
          <div className="min-w-0">
            <h3 className="m-0 text-[1.15rem] font-semibold text-[var(--theme-text)]">Release Inventory</h3>
            <p className="m-0 mt-1 text-[0.9rem] text-[var(--theme-text-muted)]">{salesOrder.soCode} - {salesOrder.customerName}</p>
          </div>
          <div className="flex shrink-0 items-start gap-3">
            <div className="hidden min-w-[240px] rounded-[12px] border border-[#b8d7c0] bg-[#f1fbf3] px-3 py-2 text-[#245c34] shadow-[0_8px_18px_rgba(36,92,52,0.08)] sm:block">
              <div className="text-[0.62rem] font-semibold uppercase tracking-[0.08em] text-[#3f7a4e]">
                Release Cost
              </div>
              <div className="text-[0.95rem] font-bold leading-tight">
                {formatPeso(fclCapital)}
              </div>
              <div className="text-[0.66rem] font-semibold leading-tight text-[#5b8063]">
                {formatKg(totalNetKilo)} released
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="grid gap-4 overflow-y-auto p-5">
          {fclSlot ? (
            <div className="grid gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-4 py-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              <SummaryTile label="FCL Slot" value={`FCL ${fclSlot.number}`} />
              <SummaryTile label="Slot Code" value={fclSlot.slotCode} strong />
              <SummaryTile
                label="Location"
                value={salesOrder.branch || '-'}
              />
              <SummaryTile
                label="Sacks"
                value={slotDraftSacks > 0 ? formatSacksQuantity(slotDraftSacks) : '-'}
              />
              <SummaryTile
                label="Gross KG"
                value={slotDraftGrossKg > 0 ? formatKg(slotDraftGrossKg) : '-'}
              />
              <SummaryTile
                label="Net KG"
                value={slotDraftNetKg > 0 ? formatKg(slotDraftNetKg) : '-'}
                highlight
                strong
              />
            </div>
          ) : null}

          <div className="grid gap-3 md:grid-cols-[220px_1fr]">
            <CustomDatePicker
              label="Release Date"
              name="releaseDate"
              value={releaseDate}
              onChange={(event) => onDateChange(event.target.value)}
            />
            <label className="grid gap-1 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]">
              Remarks
              <input
                type="text"
                value={remarks}
                className="min-h-[42px] rounded-[12px] border border-[var(--theme-border)] px-3 text-[0.9rem] text-[var(--theme-text)] outline-none focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]"
                onChange={(event) => onRemarksChange(event.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>

          <div className="grid gap-3 rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-4">
            <ReleaseInventoryAllocationTable
              inventory={inventory}
              allocations={allocations}
              allocationErrors={allocationErrors}
              hasAttemptedReleaseSave={hasAttemptedReleaseSave}
              includePreReleaseInventory={includePreReleaseInventory}
              preReleaseCandidateCount={preReleaseCandidateCount}
              onAllocationChange={onAllocationChange}
              onIncludePreReleaseInventoryChange={onIncludePreReleaseInventoryChange}
              onAutoSelectAllocations={onAutoSelectAllocations}
              onResetAllocations={onResetAllocations}
            />
            <ReleaseComputationPanel
              releasedSacks={releasedSacks}
              totalNetKilo={totalNetKilo}
              totalCapital={totalCapital}
              requiredNetKilo={slotDraftNetKg}
              expensePerSack={expensePerSack}
              totalExpense={totalExpense}
              fclCapital={fclCapital}
            />
            <ReleaseExpensePanel
              releasedSacks={releasedSacks}
              expensePerSack={expensePerSack}
              expensePerSackError={allocationErrors.expensePerSack}
              totalExpense={totalExpense}
              onExpensePerSackChange={onExpensePerSackChange}
            />
          </div>

          {allocationErrors.form ? (
            <FeedbackMessage tone="error" message={allocationErrors.form} />
          ) : null}

          <div className="flex justify-end gap-2 border-t border-[var(--theme-border-soft)] pt-4">
              <button
                type="button"
                className="inline-flex min-h-[42px] items-center justify-center rounded-[12px] border border-[var(--theme-border)] px-4 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSaving}
                className="inline-flex min-h-[42px] items-center justify-center rounded-[12px] border border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] px-4 text-[0.88rem] font-semibold text-[var(--theme-accent-strong)] transition hover:bg-[var(--theme-accent)] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onSaveDraft}
              >
                {isSaving ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex min-h-[42px] items-center justify-center rounded-[12px] bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.14)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
              >
                {isSaving ? 'Saving...' : 'Save Release'}
              </button>
          </div>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function SummaryTile({ label, value, strong = false, highlight = false }) {
  return (
    <div
      className={`flex min-h-[72px] min-w-0 flex-col justify-center rounded-[12px] border px-3 py-2.5 ${
        highlight
          ? 'border-[#80b985] bg-[#eaf8ec] shadow-[0_10px_20px_rgba(60,122,65,0.12)] ring-1 ring-[#bfe5c2]'
          : 'border-[var(--theme-border-soft)] bg-white'
      }`}
    >
      <div className={`truncate text-[0.68rem] font-semibold uppercase tracking-[0.08em] ${highlight ? 'text-[#2f6f3a]' : 'text-[var(--theme-text-subtle)]'}`}>{label}</div>
      <div className={`mt-1 truncate text-[0.95rem] leading-tight ${highlight ? 'font-bold text-[#256c32]' : strong ? 'font-bold text-[var(--theme-accent-strong)]' : 'font-semibold text-[var(--theme-text)]'}`}>{value}</div>
    </div>
  );
}

function ReleaseSlotDraftInputs({
  sacks,
  grossKg,
  disabled = false,
  onSacksChange,
  onGrossKgChange,
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:max-w-[240px]">
      <ReleaseSlotDraftField
        label="Sacks"
        value={sacks || ''}
        disabled={disabled}
        onChange={onSacksChange}
        placeholder="0"
      />
      <ReleaseSlotDraftField
        label="Gross KG"
        value={formatReleaseSlotDraftNumber(grossKg)}
        disabled={disabled}
        onChange={onGrossKgChange}
        placeholder="0.00"
      />
    </div>
  );
}

function ReleaseSlotDraftField({ label, value, disabled, onChange, placeholder }) {
  return (
    <label className="grid gap-1 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
      {label}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-[38px] min-w-0 rounded-[12px] border border-[var(--theme-border)] bg-white px-3 text-[0.86rem] font-semibold text-[var(--theme-text)] outline-none transition placeholder:text-[var(--theme-text-muted)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
        placeholder={placeholder}
      />
    </label>
  );
}

function ReleaseExpensePanel({
  releasedSacks,
  expensePerSack,
  expensePerSackError,
  totalExpense,
  onExpensePerSackChange,
}) {
  return (
    <div className="grid gap-3 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-4 md:grid-cols-[minmax(180px,260px)_1fr]">
        <label className="grid gap-1 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
          Expense Per Sack
          <div
            className={`grid min-h-[42px] grid-cols-[auto_minmax(0,1fr)] items-center overflow-hidden rounded-[12px] border bg-white transition focus-within:ring-2 ${
              expensePerSackError
                ? 'border-red-300 focus-within:border-red-400 focus-within:ring-red-100'
                : 'border-[var(--theme-border)] focus-within:border-[var(--theme-accent)] focus-within:ring-[var(--theme-accent-soft)]'
            }`}
          >
            <span className="border-r border-[var(--theme-border-soft)] px-3 text-[0.85rem] font-semibold text-[var(--theme-text-subtle)]">₱</span>
            <input
              type="text"
              inputMode="decimal"
              value={expensePerSack || ''}
              className="min-w-0 border-0 bg-transparent px-3 text-[0.9rem] outline-none"
              onChange={(event) => onExpensePerSackChange(event.target.value)}
              placeholder="0.00"
            />
          </div>
          {expensePerSackError ? (
            <span className="text-[0.75rem] font-medium text-red-600">{expensePerSackError}</span>
          ) : null}
        </label>
        <div className="mt-0 flex h-[42px] self-start items-center rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-4 text-[0.86rem] text-[var(--theme-text-muted)] md:mt-[25px]">
          <span className="font-semibold text-[var(--theme-text)]">{formatSacksQuantity(releasedSacks)}</span>
          {' × '}
          <span className="font-semibold text-[var(--theme-text)]">{formatPeso(expensePerSack || 0)}</span>
          {' = '}
          <span className="font-semibold text-[var(--theme-accent-strong)]">{formatPeso(totalExpense)}</span>
        </div>
    </div>
  );
}

function ReleaseComputationPanel({
  releasedSacks,
  totalNetKilo,
  totalCapital,
  requiredNetKilo,
  expensePerSack,
  totalExpense,
  fclCapital,
}) {
  const netKiloShortfall = Math.max(0, Number(requiredNetKilo || 0) - Number(totalNetKilo || 0));
  const hasMetNetKilo = Number(requiredNetKilo || 0) > 0 && netKiloShortfall <= 0;

  return (
    <section className="min-w-0 rounded-[18px] border border-[var(--theme-accent)] bg-[var(--theme-accent-panel)] p-4">
      <h3 className="m-0 text-[0.95rem] font-semibold text-[var(--theme-accent-panel-text)]">
        Releasing Summary
      </h3>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <ReleaseSummaryRow label="Released Sacks" value={formatSacksQuantity(releasedSacks)} />
        <ReleaseSummaryRow label="Total Net Kilo" value={formatKg(totalNetKilo)} />
        <ReleaseSummaryRow label="Required Net Kilo" value={requiredNetKilo > 0 ? formatKg(requiredNetKilo) : '-'} />
        <ReleaseSummaryRow
          label={hasMetNetKilo ? 'Net Kilo Status' : 'Net Kilo Shortfall'}
          value={hasMetNetKilo ? 'Ready' : formatKg(netKiloShortfall)}
          tone={hasMetNetKilo ? 'success' : netKiloShortfall > 0 ? 'warning' : 'neutral'}
          strong
        />
        <ReleaseSummaryRow label="Total Capital" value={formatPeso(totalCapital)} />
        <ReleaseSummaryRow label="Expense Per Sack" value={formatPeso(expensePerSack || 0)} />
        <ReleaseSummaryRow label="Total Expense" value={formatPeso(totalExpense)} />
        <ReleaseSummaryRow
          label="FCL Total Capital"
          value={formatPeso(fclCapital)}
          tone="success"
          highlight={hasMetNetKilo}
          strong
        />
      </div>
    </section>
  );
}

function ReleaseSummaryRow({ label, value, strong = false, tone = 'neutral', highlight = false }) {
  const toneClassName =
    highlight
      ? 'border-[color-mix(in_srgb,var(--theme-accent-panel-text)_45%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_12%,transparent)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] ring-2 ring-[color-mix(in_srgb,var(--theme-accent-panel-text)_28%,transparent)]'
      : tone === 'success'
      ? 'border-[color-mix(in_srgb,var(--theme-accent-panel-text)_35%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_10%,transparent)]'
      : tone === 'warning'
        ? 'border-[color-mix(in_srgb,var(--theme-accent-panel-text)_42%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_16%,transparent)]'
        : 'border-[color-mix(in_srgb,var(--theme-accent-panel-text)_34%,transparent)] bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_12%,transparent)]';
  const valueToneClassName =
    highlight
      ? 'text-[var(--theme-accent-panel-text)]'
      : tone === 'success'
      ? 'text-[var(--theme-accent-panel-text)]'
      : tone === 'warning'
        ? 'text-[var(--theme-accent-panel-text)]'
        : 'text-[var(--theme-accent-panel-text)]';

  return (
    <div className={`relative grid min-h-[76px] min-w-0 content-center gap-1 overflow-hidden rounded-[12px] border px-3 py-2.5 text-[0.84rem] transition ${highlight ? 'pr-14' : ''} ${toneClassName}`}>
      <span className="min-w-0 truncate text-[var(--theme-accent-panel-text)]">{label}</span>
      <span className={`min-w-0 truncate ${highlight ? 'text-[1.08rem]' : 'text-[1rem]'} ${strong ? 'font-bold' : 'font-semibold'} ${valueToneClassName}`}>
        {value}
      </span>
      {highlight ? (
        <CheckCircle2
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-accent-panel-text)]"
          size={34}
          strokeWidth={2.4}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

function ReleaseInventoryAllocationTable({
  inventory,
  allocations,
  allocationErrors,
  hasAttemptedReleaseSave,
  includePreReleaseInventory,
  preReleaseCandidateCount,
  onAllocationChange,
  onIncludePreReleaseInventoryChange,
  onAutoSelectAllocations,
  onResetAllocations,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const shouldHighlightEmptyQuantities =
    allocationErrors.form === 'Enter at least one quantity to release.';
  const sortedInventory = useMemo(
    () =>
      sortReleaseInventoryByProfitPriority(
        inventory.filter(
          (item) =>
            item.isPreReleaseCandidate ||
            item.isUnavailableDraftInventory ||
            Number(allocations[item.sourceKey] || 0) > 0 ||
            getAvailableSacks(item) > 0
        )
      ),
    [allocations, inventory]
  );
  const totalItems = sortedInventory.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / RELEASE_ALLOCATION_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * RELEASE_ALLOCATION_PAGE_SIZE;
  const endIndex = Math.min(startIndex + RELEASE_ALLOCATION_PAGE_SIZE, totalItems);
  const visibleInventory = sortedInventory.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="min-h-[126px] overflow-hidden rounded-[16px] border border-[var(--theme-border-soft)] bg-white">
      <div className="flex flex-col gap-2 border-b border-[var(--theme-border-soft)] px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-[0.82rem] font-semibold text-[var(--theme-text)]">
            Inventory Allocation
          </div>
          <div className="text-[0.76rem] text-[var(--theme-text-muted)]">
            Auto select uses the most profitable available sacks first.
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-[12px] border px-3 text-[0.8rem] font-semibold transition ${
              includePreReleaseInventory
                ? 'border-[#d4a64f] bg-[#fff8e6] text-[#8a6424]'
                : 'border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] hover:border-[#d4a64f] hover:bg-[#fff8e6] hover:text-[#8a6424]'
            }`}
            onClick={() => onIncludePreReleaseInventoryChange(!includePreReleaseInventory)}
            disabled={!preReleaseCandidateCount}
          >
            Pre-release POs
            <span className="rounded-full bg-white/70 px-2 py-0.5 text-[0.68rem]">
              {preReleaseCandidateCount}
            </span>
          </button>
          <button
            type="button"
            className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-[12px] border border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] px-3 text-[0.8rem] font-semibold text-[var(--theme-accent-strong)] transition hover:bg-[var(--theme-accent)] hover:text-white"
            onClick={onAutoSelectAllocations}
          >
            <Sparkles size={15} aria-hidden="true" />
            Auto Select
          </button>
          <button
            type="button"
            className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-[12px] border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[#d96d5f] hover:bg-[#fff1ec] hover:text-[#9b4e43]"
            onClick={onResetAllocations}
            title="Reset all quantities"
            aria-label="Reset all quantities"
          >
            <RotateCcw size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[940px] w-full border-collapse text-left text-[0.84rem]">
          <thead className="bg-[var(--theme-accent-panel)] text-[0.68rem] uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
            <tr>
              <th className="px-4 py-2.5">Received Date</th>
              <th className="px-4 py-2.5">Sacks Mark</th>
              <th className="px-4 py-2.5">Quantity</th>
              <th className="px-4 py-2.5">Available KG Net</th>
              <th className="px-4 py-2.5">Unit Cost</th>
              <th className="px-4 py-2.5">Capital</th>
              <th className="px-4 py-2.5">Quantity to Release by Sacks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--theme-border-soft)]">
            {visibleInventory.length ? (
              visibleInventory.map((item) => {
                const quantityError = allocationErrors[item.sourceKey];
                const isReservedByDraft = Boolean(item.isReservedByDraft);
                const availableSacks = getAvailableSacks(item);
                const maxReleaseSacks = getMaxReleaseSacks(item);
                const requestedSacks = Number(allocations[item.sourceKey] || 0);
                const availabilityWarning =
                  !quantityError &&
                  !isReservedByDraft &&
                  !item.isPreReleaseCandidate &&
                  requestedSacks > availableSacks
                    ? `Only ${availableSacks.toLocaleString('en-US')} sacks remain available.`
                    : '';
                const shouldHighlightQuantity =
                  (!isReservedByDraft && Boolean(quantityError)) ||
                  (hasAttemptedReleaseSave &&
                    shouldHighlightEmptyQuantities &&
                    !isReservedByDraft &&
                    !allocations[item.sourceKey]);

                return (
                <tr
                  key={item.sourceKey || item.id}
                  className={isReservedByDraft ? 'bg-[var(--theme-surface-soft)] text-[var(--theme-text-muted)]' : ''}
                >
                  <td className="px-4 py-2">{formatShortDate(item.receivedAt)}</td>
                  <td className="px-4 py-2">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span className="leading-tight font-semibold text-[var(--theme-text)]">
                        {getInventorySacksMark(item)}
                      </span>
                      {item.isPreReleaseCandidate ? (
                        <span className="inline-flex min-h-[20px] items-center rounded-full border border-[#ead7a8] bg-[#fff8e6] px-2 text-[0.62rem] font-bold uppercase tracking-[0.06em] text-[#8a6424]">
                          Pre-release
                        </span>
                      ) : null}
                      {item.isUnavailableDraftInventory ? (
                        <span className="inline-flex min-h-[20px] items-center rounded-full border border-[#efc7ba] bg-[#fff1ec] px-2 text-[0.62rem] font-bold uppercase tracking-[0.06em] text-[#9b4e43]">
                          Unavailable
                        </span>
                      ) : null}
                      {isReservedByDraft ? (
                        <span className="inline-flex min-h-[20px] items-center rounded-full border border-[#c7d6dc] bg-[#eef6f7] px-2 text-[0.62rem] font-bold uppercase tracking-[0.06em] text-[#526d73]">
                          Reserved
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 text-[0.72rem] leading-tight text-[var(--theme-text-muted)]">
                      {item.poCode || '-'}
                      {item.poStatus ? ` / ${item.poStatus}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-2">{formatSacksQuantity(availableSacks)}</td>
                  <td className="px-4 py-2 font-semibold text-[var(--theme-accent-strong)]">
                    {item.isPreReleaseCandidate ? (
                      <span className="font-medium text-[var(--theme-text-muted)]">Pending balancing</span>
                    ) : (
                      formatKg(item.availableKg)
                    )}
                  </td>
                  <td className="px-4 py-2">{formatPesoWithDecimals(item.unitCost)}</td>
                  <td className="px-4 py-2">
                    {item.isPreReleaseCandidate ? (
                      <span className="font-medium text-[var(--theme-text-muted)]">Pending balancing</span>
                    ) : (
                      formatPeso(getInventoryCapital(item))
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div
                      className={`grid min-h-[36px] w-[250px] grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center overflow-hidden rounded-[10px] border bg-white focus-within:ring-2 ${
                        shouldHighlightQuantity
                          ? 'border-[#d96d5f] focus-within:ring-[#f6d8d4]'
                          : 'border-[var(--theme-border)] focus-within:border-[var(--theme-accent)] focus-within:ring-[var(--theme-accent-soft)]'
                      }`}
                    >
                      <button
                        type="button"
                        className="min-h-[36px] border-r border-[var(--theme-border-soft)] px-2 text-[0.68rem] font-bold uppercase text-[var(--theme-accent)] transition hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
                        disabled={isReservedByDraft}
                        onClick={() => onAllocationChange(item, '0')}
                      >
                        Min
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={allocations[item.sourceKey] || ''}
                        disabled={isReservedByDraft}
                        className="min-w-0 border-0 bg-transparent px-3 text-[0.84rem] outline-none disabled:cursor-not-allowed disabled:text-[var(--theme-text-muted)]"
                        onChange={(event) => onAllocationChange(item, event.target.value)}
                        placeholder={isReservedByDraft ? 'Reserved' : '0'}
                      />
                      <button
                        type="button"
                        className="min-h-[36px] border-l border-[var(--theme-border-soft)] px-2 text-[0.68rem] font-bold uppercase text-[var(--theme-accent)] transition hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)]"
                        disabled={isReservedByDraft}
                        onClick={() => onAllocationChange(item, String(Math.floor(maxReleaseSacks)))}
                      >
                        Max
                      </button>
                      <div className="grid min-h-[36px] place-items-center border-l border-[var(--theme-border-soft)] bg-white px-2 text-right">
                        <span className="text-[0.68rem] font-semibold uppercase leading-none text-[var(--theme-text-subtle)]">
                          sacks
                        </span>
                      </div>
                    </div>
                    {quantityError ? (
                      <div className="mt-1 text-[0.72rem] leading-tight text-[#9b4e43]">
                        {quantityError}
                      </div>
                    ) : availabilityWarning ? (
                      <div className="mt-1 text-[0.72rem] leading-tight text-[#8a6424]">
                        {availabilityWarning}
                      </div>
                    ) : isReservedByDraft ? (
                      <div className="mt-1 text-[0.72rem] leading-tight text-[#526d73]">
                        Already saved in another release draft.
                      </div>
                    ) : null}
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td className="px-4 py-8 text-center text-[0.86rem] text-[var(--theme-text-muted)]" colSpan={7}>
                  No available inventory is ready for release.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationFooter
        currentPage={normalizedPage}
        endItem={endIndex}
        isNextDisabled={normalizedPage >= totalPages}
        isPreviousDisabled={normalizedPage <= 1}
        onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
        onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
        startItem={totalItems ? startIndex + 1 : 0}
        totalItems={totalItems}
        totalPages={totalPages}
      />
    </div>
  );
}

function EmptyPanel({ title, message }) {
  return (
    <div className="rounded-[18px] border border-[var(--theme-border-soft)] bg-white px-5 py-8 text-center shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)]">
      <h3 className="m-0 text-[1rem] font-semibold text-[var(--theme-text)]">{title}</h3>
      <p className="mx-auto mt-2 max-w-[560px] text-[0.92rem] leading-6 text-[var(--theme-text-muted)]">{message}</p>
    </div>
  );
}

function StatusPill({ label }) {
  const normalizedLabel = String(label || 'Released');
  const className = normalizedLabel.includes('Pending')
    ? 'border-[#ead7ad] bg-[#fff8e8] text-[#8a641f]'
    : normalizedLabel.includes('Draft')
      ? 'border-[#bfd0eb] bg-[#eef5ff] text-[#315f9b]'
    : normalizedLabel.includes('Partial')
      ? 'border-[var(--theme-border)] bg-[var(--theme-accent-soft)] text-[var(--theme-accent-strong)]'
      : 'border-[#cfe3ce] bg-[#f1f8ef] text-[#4f7650]';

  return (
    <span className={`inline-flex min-h-[28px] items-center rounded-full border px-3 text-[0.76rem] font-semibold ${className}`}>
      {normalizedLabel}
    </span>
  );
}

function formatKg(value) {
  return `${formatNumber(value)} kg`;
}

function formatSalesOrderQuantity(value) {
  return `${formatNumber(value)} FCL`;
}

function sortReleaseInventoryByProfitPriority(inventory) {
  return [...inventory].sort((firstItem, secondItem) => {
    const firstUnitCost = Number(firstItem.unitCost || 0);
    const secondUnitCost = Number(secondItem.unitCost || 0);

    return (
      firstUnitCost - secondUnitCost ||
      new Date(firstItem.receivedAt || 0).getTime() -
        new Date(secondItem.receivedAt || 0).getTime() ||
      String(firstItem.poCode || '').localeCompare(String(secondItem.poCode || ''))
    );
  });
}

function formatInventoryQuantity(item) {
  const label = String(item.quantityLabel || '').trim();

  if (label) {
    return label;
  }

  const unit = String(item.quantityUnit || '').trim().toLowerCase() === 'sacks' ? 'sacks' : 'kg';

  return `${formatNumber(item.quantity)} ${unit}`;
}

function getInventorySacksMark(item) {
  const sacksMark = String(item.sacksMark || '').trim();

  if (sacksMark) {
    return sacksMark;
  }

  const poCode = String(item.poCode || '').trim();
  const [markFromPoCode] = poCode.split('-');

  if (markFromPoCode) {
    return markFromPoCode;
  }

  const [markFromSupplier] = String(item.supplierName || '').split(' - ');
  return String(markFromSupplier || '').trim() || '-';
}

function getReleaseItemStatusLabel(item) {
  const status = String(item.poStatus || '').trim();

  if (status) {
    return status;
  }

  if (String(item.sourceType || '').toLowerCase() === 'merge' || item.mergeId) {
    return 'Merged Inventory';
  }

  return '-';
}

function getAvailableSacks(item) {
  if (item.isPreReleaseCandidate) {
    return Math.floor(getInventorySacksQuantity(item));
  }

  const availableKg = Number(item.availableKg || 0);
  const originalKg = Number(item.originalKg || item.originalQuantityKg || 0);
  const originalSacks = getInventorySacksQuantity(item);

  if (availableKg <= 0) {
    return 0;
  }

  if (originalKg > 0 && originalSacks > 0) {
    return clampWholeSacks((availableKg / originalKg) * originalSacks, originalSacks);
  }

  return Math.floor(originalSacks);
}

function getReleasedSacks(item) {
  const releasedKg = Number(item.releasedKg || 0);
  const originalKg = Number(item.originalKg || item.originalQuantityKg || 0);
  const originalSacks = getInventorySacksQuantity(item);

  if (releasedKg <= 0 || originalSacks <= 0) {
    return 0;
  }

  if (originalKg > 0) {
    return Math.max(originalSacks - getAvailableSacks(item), 0);
  }

  return Math.max(originalSacks - getAvailableSacks(item), 0);
}

function clampWholeSacks(value, maxValue) {
  const roundedValue = Math.round(Number(value || 0) * 1000000) / 1000000;
  const wholeSacks = Math.floor(roundedValue);

  return Math.min(Math.max(wholeSacks, 0), Math.floor(Number(maxValue || 0)));
}

function getMaxReleaseSacks(item) {
  if (item.isReservedByDraft) {
    return 0;
  }

  if (item.isPreReleaseCandidate) {
    const draftReleaseQuantity = Number(item.draftReleaseQuantity || 0);

    if (draftReleaseQuantity > 0) {
      return Math.floor(draftReleaseQuantity);
    }

    return Math.floor(getInventorySacksQuantity(item));
  }

  return getAvailableSacks(item);
}

function getInventorySacksQuantity(item) {
  const label = String(item.quantityLabel || '').trim();
  const labelMatch = label.match(/[\d,.]+/);
  const unit = String(item.quantityUnit || '').trim().toLowerCase();

  if (labelMatch && (!unit || unit === 'sacks' || label.toLowerCase().includes('sack'))) {
    return Number(labelMatch[0].replace(/,/g, ''));
  }

  const quantity = Number(item.quantity || item.originalQuantity || 0);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}

function getInventoryCapital(item) {
  return Number(item.availableKg || 0) * Number(item.unitCost || 0);
}

function formatSacksQuantity(value) {
  return `${formatNumber(value)} sacks`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPesoWithDecimals(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function filterPendingSalesOrders(salesOrders, filters) {
  const normalizedSearch = normalizeSearchValue(filters.search);

  return salesOrders.filter((salesOrder) => {
    const matchesSearch =
      !normalizedSearch ||
      [
        salesOrder.soCode,
        salesOrder.batchNo,
        salesOrder.batchNumber,
        salesOrder.customerName,
        salesOrder.branch,
        salesOrder.status,
        formatSalesOrderQuantity(salesOrder.requestedQuantity),
        formatSalesOrderQuantity(salesOrder.releasedQuantity),
        formatKg(salesOrder.releasedKg ?? salesOrder.releasedQuantity),
        formatSalesOrderQuantity(salesOrder.remainingQuantity),
      ].some((value) => normalizeSearchValue(value).includes(normalizedSearch));
    const matchesStatus =
      filters.pendingStatus === 'All' || salesOrder.status === filters.pendingStatus;

    return matchesSearch && matchesStatus;
  });
}

function filterReleaseHistory(releases, filters) {
  const normalizedSearch = normalizeSearchValue(filters.search);

  return releases.filter((release) => {
    if (String(release.status || '') === 'Pre-Released') {
      return false;
    }

    const matchesSearch =
      !normalizedSearch ||
      [
        release.releaseCode,
        release.slotCode,
        release.soCode,
        release.customerName,
        release.fclBatchNumber,
        release.containerNumber,
        release.sourcePoCodes,
        release.supplierNames,
        release.preparedBy,
        release.status,
        formatKg(release.releasedKg),
      ].some((value) => normalizeSearchValue(value).includes(normalizedSearch));
    const matchesDate =
      !filters.date || toDateInputValue(release.releaseDate) === filters.date;

    return matchesSearch && matchesDate;
  });
}

function filterAvailableInventory(inventory, filters) {
  const normalizedSearch = normalizeSearchValue(filters.search);

  return inventory
    .filter((item) => {
      if (!isCompletedAvailableInventory(item)) {
        return false;
      }

      const matchesSearch =
        !normalizedSearch ||
        [
          item.poCode,
          item.supplierName,
          item.warehouseLocation,
          formatKg(item.originalKg),
          formatSacksQuantity(getReleasedSacks(item)),
          formatKg(item.releasedKg),
          formatSacksQuantity(getAvailableSacks(item)),
          formatKg(item.availableKg),
          formatPeso(item.unitCost),
        ].some((value) => normalizeSearchValue(value).includes(normalizedSearch));
      const matchesDate =
        !filters.date || toDateInputValue(item.receivedAt) === filters.date;

      return matchesSearch && matchesDate;
    })
    .sort(sortInventoryByNewestReceived);
}

function sortInventoryByNewestReceived(firstItem, secondItem) {
  return (
    new Date(secondItem.receivedAt || 0).getTime() -
      new Date(firstItem.receivedAt || 0).getTime() ||
    String(secondItem.poCode || '').localeCompare(String(firstItem.poCode || ''))
  );
}

function isCompletedAvailableInventory(item) {
  const sourceType = String(item.sourceType || '').trim().toLowerCase();
  const isMergedInventory = sourceType === 'merge' || Number(item.mergeId || 0) > 0;

  return (
    !item.isPreReleaseCandidate &&
    (isMergedInventory || String(item.poStatus || '').trim() === 'Completed')
  );
}

function buildReleasingSearchSuggestions({
  activeTab,
  pendingSalesOrders,
  releaseHistory,
  availableInventory,
}) {
  if (activeTab === 'history') {
    const finalReleases = releaseHistory.filter(
      (release) => String(release.status || '') !== 'Pre-Released'
    );

    return buildSuggestionList(
      finalReleases.flatMap((release) => [
        ['release-code', release.releaseCode, `Release No. / ${release.soCode || 'Batch'}`],
        ['slot-code', release.slotCode, 'Slot Code'],
        ['so-code', release.soCode, release.customerName || 'Sales Order'],
        ['customer', release.customerName, 'Customer'],
        ['fcl-batch', release.fclBatchNumber, release.containerNumber || 'FCL'],
        ['container', release.containerNumber, release.fclBatchNumber || 'Container'],
        ['source-po', release.sourcePoCodes, 'Source P.O.'],
        ['supplier', release.supplierNames, 'Supplier'],
      ])
    );
  }

  if (activeTab === 'inventory') {
    const completedInventory = availableInventory.filter(isCompletedAvailableInventory);

    return buildSuggestionList(
      completedInventory.flatMap((item) => [
        ['po-code', item.poCode, item.supplierName || 'P.O.'],
        ['supplier', item.supplierName, 'Supplier'],
        ['warehouse', item.warehouseLocation, 'Warehouse'],
        ['type', item.seaweedType, 'Type'],
      ])
    );
  }

  return buildSuggestionList(
    pendingSalesOrders.flatMap((salesOrder) => [
      ['so-code', getSalesOrderBatchNo(salesOrder), salesOrder.customerName || 'Sales Order'],
      ['customer', salesOrder.customerName, 'Customer'],
      ['branch', salesOrder.branch, 'Branch'],
      ['status', salesOrder.status, 'Status'],
    ])
  );
}

function buildSuggestionList(entries) {
  const suggestionsByKey = new Map();

  entries.forEach(([group, value, detail]) => {
    const normalizedValue = String(value || '').trim();

    if (!normalizedValue) {
      return;
    }

    const key = `${group}:${normalizedValue.toLowerCase()}`;
    suggestionsByKey.set(key, {
      group,
      value: normalizedValue,
      label: normalizedValue,
      detail,
    });
  });

  return Array.from(suggestionsByKey.values())
    .sort((firstSuggestion, secondSuggestion) => {
      const firstPriority = getSuggestionGroupPriority(firstSuggestion.group);
      const secondPriority = getSuggestionGroupPriority(secondSuggestion.group);

      return (
        firstPriority - secondPriority ||
        firstSuggestion.label.localeCompare(secondSuggestion.label)
      );
    })
    .slice(0, 18);
}

function getSuggestionGroupPriority(group) {
  if (group === 'release-code') {
    return 0;
  }

  return 1;
}

function normalizeSearchValue(value) {
  return String(value || '').trim().toLowerCase();
}

function getSalesOrderBatchNo(salesOrder) {
  return salesOrder?.soCode || salesOrder?.batchNo || salesOrder?.batchNumber || '';
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export default ReleasingPage;
